<template>
  <div class="bg-surface-elevated border border-gray-200 rounded-lg overflow-hidden">
    <header class="card-header flex items-center justify-between">
      <div class="flex items-center gap-2 min-w-0">
        <EditorIcon name="search" class="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div class="min-w-0">
          <h2 class="font-medium text-gray-900 truncate">
            SEO · {{ page.title }}
          </h2>
          <p class="text-[11px] text-gray-500 truncate">
            How this page appears in Google &amp; social shares
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <span
          :class="['text-[11px] px-2 py-0.5 rounded-full font-medium', scoreClass]"
          :title="`${score.passed}/${score.total} checks passed`">
          SEO {{ score.passed }}/{{ score.total }}
        </span>
        <button
          type="button"
          class="btn btn-ghost btn-icon btn-xs"
          aria-label="Close SEO editor"
          title="Close SEO editor"
          @click="$emit('close')">
          <EditorIcon name="close" class="w-4 h-4" />
        </button>
      </div>
    </header>

    <!-- Tabs -->
    <div class="tabs px-2" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.id"
        type="button"
        role="tab"
        class="tab"
        :aria-selected="tab === t.id"
        :class="{ active: tab === t.id }"
        @click="tab = t.id">
        {{ t.label }}
      </button>
    </div>

    <div class="p-4 space-y-5">
      <!-- ===================== SEARCH ===================== -->
      <template v-if="tab === 'search'">
        <!-- Google result preview -->
        <div class="rounded-lg border border-gray-200 p-3 bg-gray-50">
          <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">
            Google preview
          </p>
          <div class="text-[#202124]">
            <div class="text-xs text-[#4d5156] truncate">
              {{ displayUrl }}
            </div>
            <div class="text-[#1a0dab] text-lg leading-tight truncate">
              {{ eff.title }}
            </div>
            <div class="text-[13px] text-[#4d5156] leading-snug line-clamp-2">
              {{ eff.description || 'Add a meta description to control this snippet.' }}
            </div>
          </div>
        </div>

        <label class="block">
          <span class="form-label flex items-center justify-between mb-1">
            Page title
            <span :class="counterClass(model.meta_title, 30, 60)">{{ (model.meta_title || '').length }}/60</span>
          </span>
          <input
            v-model="model.meta_title"
            type="text"
            :placeholder="fallback.title"
            class="form-input w-full"
            @input="scheduleSave">
          <span class="form-helper">Falls back to “{{ fallback.title }}”. Aim for 30–60 characters.</span>
        </label>

        <label class="block">
          <span class="form-label flex items-center justify-between mb-1">
            Meta description
            <span :class="counterClass(model.meta_description, 70, 160)">{{ (model.meta_description || '').length }}/160</span>
          </span>
          <textarea
            v-model="model.meta_description"
            rows="3"
            :placeholder="fallback.description || 'Summarize the page in 1–2 sentences.'"
            class="form-input w-full"
            @input="scheduleSave" />
          <span class="form-helper">Aim for 70–160 characters.</span>
        </label>
      </template>

      <!-- ===================== SOCIAL ===================== -->
      <template v-else-if="tab === 'social'">
        <!-- Social card preview -->
        <div class="rounded-lg border border-gray-200 overflow-hidden">
          <p class="text-[10px] uppercase tracking-wide text-gray-400 px-3 pt-2">
            Social share preview
          </p>
          <div class="p-3">
            <div class="border border-gray-200 rounded-lg overflow-hidden max-w-sm">
              <div class="aspect-[1.91/1] bg-gray-100 flex items-center justify-center overflow-hidden">
                <img v-if="eff.ogImage" :src="eff.ogImage" alt="" class="w-full h-full object-cover">
                <span v-else class="text-xs text-gray-400">No share image set</span>
              </div>
              <div class="px-3 py-2 bg-white">
                <div class="text-[10px] uppercase text-gray-400 truncate">
                  {{ displayHost }}
                </div>
                <div class="text-sm font-medium text-gray-900 truncate">
                  {{ eff.ogTitle }}
                </div>
                <div class="text-xs text-gray-500 line-clamp-2">
                  {{ eff.ogDescription }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <label class="block">
          <span class="form-label mb-1 block">Share image</span>
          <div class="flex items-center gap-2">
            <input
              v-model="model.og_image_url"
              type="text"
              :placeholder="seoDefaults.default_og_image_url || 'https://…/share.jpg'"
              class="form-input flex-1"
              @input="scheduleSave">
            <button
              type="button"
              :disabled="uploading"
              class="btn btn-secondary btn-sm"
              @click="$refs.ogFile.click()">
              {{ uploading ? 'Uploading…' : 'Upload' }}
            </button>
            <input ref="ogFile" type="file" accept="image/*" class="hidden" @change="onUpload">
          </div>
          <span class="form-helper">Recommended 1200×630. Falls back to the site default.</span>
        </label>

        <label class="block">
          <span class="form-label mb-1 block">Social title</span>
          <input
            v-model="model.og_title"
            type="text"
            :placeholder="eff.title"
            class="form-input w-full"
            @input="scheduleSave">
        </label>

        <label class="block">
          <span class="form-label mb-1 block">Social description</span>
          <textarea
            v-model="model.og_description"
            rows="2"
            :placeholder="eff.description"
            class="form-input w-full"
            @input="scheduleSave" />
        </label>

        <label class="block">
          <span class="form-label mb-1 block">Twitter card</span>
          <select
            v-model="model.twitter_card"
            class="form-select w-full"
            @change="scheduleSave">
            <option :value="null">Auto (large image when set)</option>
            <option value="summary_large_image">Large image</option>
            <option value="summary">Small thumbnail</option>
          </select>
        </label>
      </template>

      <!-- ===================== ADVANCED ===================== -->
      <template v-else>
        <label class="block">
          <span class="form-label mb-1 block">Canonical URL</span>
          <input
            v-model="model.canonical_url"
            type="text"
            :placeholder="canonicalDefault"
            class="form-input w-full"
            @input="scheduleSave">
          <span class="form-helper">Leave blank to use this page’s own URL ({{ canonicalDefault }}).</span>
        </label>

        <div class="flex items-center gap-6">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input v-model="model.robots_index" type="checkbox" class="form-checkbox" @change="scheduleSave">
            Allow search engines to index this page
          </label>
        </div>
        <div class="flex items-center gap-6">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input v-model="model.robots_follow" type="checkbox" class="form-checkbox" @change="scheduleSave">
            Follow links on this page
          </label>
        </div>

        <div class="border-t border-gray-100 pt-4">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700 mb-2">
            <input v-model="model.schema_enabled" type="checkbox" class="form-checkbox" @change="scheduleSave">
            Add structured data (rich results)
          </label>
          <label v-if="model.schema_enabled" class="block mt-2">
            <span class="form-label mb-1 block">Schema type</span>
            <select
              v-model="model.schema_type"
              class="form-select w-full"
              @change="scheduleSave">
              <option :value="null">Auto (LocalBusiness on home)</option>
              <option v-for="t in schemaTypes" :key="t" :value="t">{{ t }}</option>
            </select>
            <span class="form-helper">Home pages auto-emit a LocalBusiness profile from your business info.</span>
          </label>
        </div>
      </template>

      <!-- Checklist -->
      <ul class="border-t border-gray-100 pt-3 space-y-1.5">
        <li v-for="c in score.checks" :key="c.label" class="flex items-start gap-2 text-xs">
          <span :class="c.ok ? 'text-green-600' : 'text-gray-300'">{{ c.ok ? '✓' : '○' }}</span>
          <span :class="c.ok ? 'text-gray-500' : 'text-gray-700'">{{ c.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  page: { type: Object, required: true },
  seoDefaults: { type: Object, default: () => ({}) },
  uploadImage: { type: Function, default: null }
})

const emit = defineEmits(['save', 'close'])

const tabs = [
  { id: 'search', label: 'Search' },
  { id: 'social', label: 'Social' },
  { id: 'advanced', label: 'Advanced' }
]
const tab = ref('search')
// Allowed schema types come from the server (WebsitePage::SCHEMA_TYPES) so the
// picker can't offer a value the model would reject.
const schemaTypes = computed(() => props.seoDefaults.schema_types || ['LocalBusiness', 'Service', 'WebPage', 'Article'])

function blankModel(seo) {
  return {
    meta_title: seo.meta_title || '',
    meta_description: seo.meta_description || '',
    canonical_url: seo.canonical_url || '',
    robots_index: seo.robots_index !== false,
    robots_follow: seo.robots_follow !== false,
    robots_advanced: seo.robots_advanced || '',
    og_title: seo.og_title || '',
    og_description: seo.og_description || '',
    og_image_url: seo.og_image_url || '',
    og_type: seo.og_type || null,
    twitter_card: seo.twitter_card || null,
    twitter_title: seo.twitter_title || '',
    twitter_description: seo.twitter_description || '',
    twitter_image_url: seo.twitter_image_url || '',
    schema_type: seo.schema_type || null,
    schema_enabled: seo.schema_enabled !== false
  }
}

const model = reactive(blankModel(props.page.seo || {}))

// Re-seed when the page changes under us (e.g. switching pages while open).
watch(() => props.page.id, () => Object.assign(model, blankModel(props.page.seo || {})))

// Auto-derived fallbacks from the page content (Phase D) — what the build will
// use when a field is blank. Mirrors BuildSite precedence so the preview is honest.
const derived = computed(() => props.page.seo?.derived || {})

const fallback = computed(() => {
  const brand = props.seoDefaults.business_name
  const d = derived.value
  let title
  if (d.title) {
    // Interior pages brand the derived H1; home keeps the business name.
    title = props.page.kind === 'home' || !brand || d.title.includes(brand) ? d.title : `${d.title} | ${brand}`
  } else {
    const titleParts = [props.page.title, brand].filter(Boolean)
    title = props.page.kind === 'home' && brand ? brand : titleParts.join(' | ')
  }
  return {
    title,
    description: d.description || props.seoDefaults.description || ''
  }
})

const eff = computed(() => {
  const title = model.meta_title || fallback.value.title
  const description = model.meta_description || fallback.value.description
  return {
    title,
    description,
    ogTitle: model.og_title || title,
    ogDescription: model.og_description || description,
    ogImage: model.og_image_url || props.seoDefaults.default_og_image_url || derived.value.image || ''
  }
})

const displayHost = computed(() => {
  const base = props.seoDefaults.base_url
  if (!base) return 'yoursite.com'
  return base.replace(/^https?:\/\//, '').replace(/\/$/, '')
})
const displayUrl = computed(() => `${displayHost.value}${props.page.path === '/' ? '' : props.page.path || ''}`)
const canonicalDefault = computed(() => {
  const base = props.seoDefaults.base_url
  return base ? `${base.replace(/\/$/, '')}${props.page.path || '/'}` : 'this page’s URL'
})

// Score the EFFECTIVE values (explicit OR auto-derived) — what actually ships —
// so a page with good content scores well before anyone edits its SEO.
const score = computed(() => {
  const t = (eff.value.title || '').length
  const d = (eff.value.description || '').length
  const checks = [
    { label: 'Page title set', ok: !!eff.value.title },
    { label: 'Title length 30–60 characters', ok: t >= 30 && t <= 60 },
    { label: 'Meta description set', ok: d > 0 },
    { label: 'Description length 70–160 characters', ok: d >= 70 && d <= 160 },
    { label: 'Social share image set', ok: !!eff.value.ogImage },
    { label: 'Page is indexable', ok: model.robots_index !== false }
  ]
  return { checks, passed: checks.filter((c) => c.ok).length, total: checks.length }
})

const scoreClass = computed(() => {
  const r = score.value.passed / score.value.total
  if (r >= 0.83) return 'bg-green-100 text-green-700'
  if (r >= 0.5) return 'bg-amber-100 text-amber-700'
  return 'bg-red-100 text-red-700'
})

function counterClass(val, min, max) {
  const n = (val || '').length
  if (n === 0) return 'text-gray-300'
  return n >= min && n <= max ? 'text-green-600' : 'text-amber-600'
}

// Debounced save — collapse blank strings to null so the server stores nil and
// the field falls back to the template/site default.
let timer = null
function scheduleSave() {
  clearTimeout(timer)
  timer = setTimeout(emitSave, 500)
}
function emitSave() {
  const out = {}
  for (const [k, v] of Object.entries(model)) {
    out[k] = typeof v === 'string' && v.trim() === '' ? null : v
  }
  emit('save', out)
}

const uploading = ref(false)
async function onUpload(e) {
  const file = e.target.files && e.target.files[0]
  if (!file || !props.uploadImage) return
  uploading.value = true
  try {
    const res = await props.uploadImage(file, `${props.page.title} share image`)
    if (res && res.url) {
      model.og_image_url = res.url
      emitSave()
    }
  } finally {
    uploading.value = false
    e.target.value = ''
  }
}
</script>
